import {useEffect, useMemo, useState} from 'react';
import {useVersionBlob} from './useVersionBlob';
import {fetchVersionDiffFromApi} from '../../../utils/api/reportVersions';

/**
 * Builds a `blockId -> 'added'|'removed'|'changed'` map from a ydoc-version-manager diff payload
 * ({diff: {added, removed, changed}, currentVersion}). Pure/exported for unit testing.
 *
 * @param {{diff?: {added?: Array, removed?: Array, changed?: Array}}|null} diffResult
 * @returns {Map<string, 'added'|'removed'|'changed'>}
 */
export function buildDiffMap(diffResult) {
    const map = new Map();
    const diff = diffResult?.diff;
    if (!diff) return map;
    (diff.added || []).forEach((b) => b?.blockId && map.set(b.blockId, 'added'));
    (diff.changed || []).forEach((b) => b?.blockId && map.set(b.blockId, 'changed'));
    (diff.removed || []).forEach((b) => b?.blockId && map.set(b.blockId, 'removed'));
    return map;
}

/**
 * Fetches both version blobs being compared plus the server-computed block-level diff between
 * them, and builds a `blockId -> 'added'|'removed'|'changed'` map for highlighting.
 *
 * Diffing is text-only per ProseMirror block (paragraphs/headings keyed by `attrs.blockId`);
 * formatting/marks are ignored, and container nodes (lists/tables/blockquotes) are not
 * individually diffed — see services/ydoc-version-manager/pmDiff.js.
 *
 * @param {string} reportId
 * @param {string|number} versionA - base/older version being compared from
 * @param {string|number} versionB - target/newer version being compared to (the "current" side)
 * @returns {{
 *   contentA: Object|null, referencesA: Array, metadataA: Object|null, loadingA: boolean, errorA: Error|null,
 *   contentB: Object|null, referencesB: Array, metadataB: Object|null, loadingB: boolean, errorB: Error|null,
 *   diffMap: Map<string, 'added'|'removed'|'changed'>,
 *   removedBlocks: Array,
 *   loading: boolean,
 *   error: Error|null,
 * }}
 */
export function useVersionDiff(reportId, versionA, versionB) {
    const {
        content: contentA,
        references: referencesA,
        metadata: metadataA,
        loading: loadingA,
        error: errorA,
    } = useVersionBlob(reportId, versionA);

    const {
        content: contentB,
        references: referencesB,
        metadata: metadataB,
        loading: loadingB,
        error: errorB,
    } = useVersionBlob(reportId, versionB);

    const [diffResult, setDiffResult] = useState(null);
    const [diffLoading, setDiffLoading] = useState(false);
    const [diffError, setDiffError] = useState(null);

    useEffect(() => {
        if (!reportId || versionA == null || versionB == null) {
            setDiffResult(null);
            setDiffError(null);
            setDiffLoading(false);
            return;
        }
        let cancelled = false;
        setDiffLoading(true);
        setDiffError(null);
        fetchVersionDiffFromApi(reportId, versionA, versionB)
            .then((result) => {
                if (cancelled) return;
                setDiffResult(result);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('[useVersionDiff] Error fetching diff:', err);
                setDiffError(err);
                setDiffResult(null);
            })
            .finally(() => {
                if (!cancelled) setDiffLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [reportId, versionA, versionB]);

    const diffMap = useMemo(() => buildDiffMap(diffResult), [diffResult]);

    const removedBlocks = useMemo(() => diffResult?.diff?.removed || [], [diffResult]);

    return {
        contentA,
        referencesA,
        metadataA,
        loadingA,
        errorA,
        contentB,
        referencesB,
        metadataB,
        loadingB,
        errorB,
        diffMap,
        removedBlocks,
        loading: loadingA || loadingB || diffLoading,
        error: errorA || errorB || diffError,
    };
}

export default useVersionDiff;
